import { computed, ref } from 'vue'

import {
  CREATE_PROPERTY_SUCCESS_MESSAGE,
  DELETE_PROPERTY_SUCCESS_MESSAGE,
  DELETE_PROPERTY_VALUE_SUCCESS_MESSAGE,
  UPDATE_PROPERTY_SUCCESS_MESSAGE,
} from '@/modules/Classes/propertyMessages'
import { useTeamProperties } from '@/modules/Classes/useTeamProperties'
import { useStageView } from '@/modules/Workview/useStageView'
import { useAnnotationClassProperty } from '@/modules/Classes/useAnnotationClassProperty'
import { getDescendantIds, isPropertyAllowedByAncestry } from '@/modules/Classes/propertyVisibility'
import { useToast } from '@/uiKit/Toast/useToast'
import { useTeamStore } from '@/pinia/useTeamStore'
import { setContext } from '@/services/sentry'
import type {
  PropertyValue,
  TeamItemProperty,
  TeamProperty,
  TeamPropertyValue,
} from '@/store/types/PropertyTypes'
import { TeamPropertyGranularity } from '@/store/types/PropertyTypes'
import type {
  DatasetItemPropertiesResponse,
  DatasetItemPropertyValues,
  TeamPropertyRequestBody,
} from '@/backend/darwin/teamProperties'
import {
  createTeamProperty,
  deleteTeamProperty,
  deleteTeamPropertyValue,
  getItemTeamItemProperties,
  reorderTeamPropertyValues,
  setItemTeamPropertyValue,
  type TeamPropertyUpdateRequestBody,
  updateTeamProperty,
  updateTeamPropertyValue,
} from '@/backend/darwin/teamProperties'
import { getErrorMessage } from '@/backend/error'
import type { ParsedError } from '@/backend/error/types'
import type { V2WorkflowCommandResponse } from '@/store/types/V2WorkflowCommandResponse'
import type { ApiResponse } from '@/store/types'
import { useWorkflowInWorkviewStore } from '@/modules/Workview/useWorkflowInWorkviewStore'
import { defineComposable } from '@/core/utils/defineComposable'
import type { V2WorkflowStageInstancePayload } from '@/store/types/V2WorkflowStageInstancePayload'
import type { V2WorkflowStagePayload } from '@/store/types/V2WorkflowStagePayload'
import { useAuth } from '@/modules/Auth/useAuth'
import { useStageSpecificClasses } from '@/composables/useStageSpecificClasses'
import { StageType } from '@/store/types/StageType'

export {
  CREATE_PROPERTY_SUCCESS_MESSAGE,
  DELETE_PROPERTY_SUCCESS_MESSAGE,
  DELETE_PROPERTY_VALUE_SUCCESS_MESSAGE,
  UPDATE_PROPERTY_SUCCESS_MESSAGE,
} from '@/modules/Classes/propertyMessages'

class UpdateError extends Error {}

/** Store used to create, update or delete individual properties */
export const useTeamProperty = defineComposable(() => {
  const teamStore = useTeamStore()
  const toast = useToast()
  const teamProperties = useTeamProperties()
  const { toTeamPropertyUpdateBody } = useAnnotationClassProperty()
  const workflowStore = useWorkflowInWorkviewStore()
  const auth = useAuth()
  const stageSpecificClasses = useStageSpecificClasses()

  const teamSlug = computed(() => teamStore.currentTeam?.slug)

  // Track pending property refreshes per itemId to prevent race conditions
  // when multiple property updates fail concurrently
  const pendingPropertyRefreshes = ref<Set<string>>(new Set())

  const defaultPropertyGranularity = ref(TeamPropertyGranularity.ANNOTATION)

  const createProperty = async (
    property: TeamPropertyRequestBody,
  ): Promise<Awaited<ReturnType<typeof createTeamProperty>> | void> => {
    if (!teamSlug.value) {
      return
    }

    const response = await createTeamProperty(teamSlug.value, property)
    if ('error' in response) {
      const message = getErrorMessage(response.error).join(', ')
      toast.error({ meta: { title: message } })
    } else {
      // Add created property to the list of properties
      teamProperties.setProperties([...teamProperties.properties, response.data])
      toast.success({ meta: { title: CREATE_PROPERTY_SUCCESS_MESSAGE } })
    }

    return response
  }

  /**
   * Return the main body to update.
   *
   * Since the main body cannot update property values in place, we strip
   * already-created values from the request; new values are still created
   * through the main body while existing values are updated via a separate
   * value-update request.
   *
   * Immutable fields (`type`, `granularity`, `annotation_class_id`,
   * `parent_property_id`, `trigger_condition`) are never included — see
   * `toTeamPropertyUpdateBody` for the contract.
   */
  const getBodyToUpdate = (
    property: Omit<TeamProperty, 'team_id' | 'slug'>,
  ): TeamPropertyUpdateRequestBody => {
    const currentPropIndex = teamProperties.properties.findIndex((p) => p.id === property.id)

    if (currentPropIndex < 0) {
      return toTeamPropertyUpdateBody(property)
    }

    const currentProp = teamProperties.properties[currentPropIndex]

    currentProp.property_values.forEach((existingValue) => {
      const propertyToUpdate = property.property_values.findIndex((p) => p.id === existingValue.id)
      if (propertyToUpdate < 0) {
        return
      }
      property.property_values.splice(propertyToUpdate, 1)
    })

    return toTeamPropertyUpdateBody(property)
  }

  const updatePropertyValuesOrder = async (
    propertyId: TeamProperty['id'],
    orderedIds: TeamPropertyValue['id'][],
  ): Promise<void> => {
    if (!teamSlug.value) {
      return
    }
    const response = await reorderTeamPropertyValues(teamSlug.value, propertyId, orderedIds)
    if ('error' in response) {
      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      throw new UpdateError(message)
    }
  }

  const updatePropertyValue = async (
    propertyId: TeamItemProperty['id'],
    propertyValue: PropertyValue,
  ): Promise<Awaited<ReturnType<typeof updateTeamPropertyValue>> | void> => {
    if (!teamSlug.value) {
      return
    }

    const response = await updateTeamPropertyValue(teamSlug.value, propertyId, propertyValue.id, {
      color: propertyValue.color,
      value: propertyValue.name,
    })
    if ('error' in response) {
      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      throw new UpdateError(message)
    }
  }

  /**
   * NOTE: updateProperty will be updated as soon as API implements the endpoint
   * for batch property values update
   */
  const updateProperty = async (
    property: Omit<TeamProperty, 'team_id' | 'slug'>,
  ): Promise<Awaited<ReturnType<typeof updateTeamProperty>> | void> => {
    if (!teamSlug.value) {
      return
    }
    const teamSlugValue = teamSlug.value

    const payloadBody = getBodyToUpdate(property)

    try {
      const propertyUpdateResponse = await updateTeamProperty(
        teamSlugValue,
        property.id,
        payloadBody,
      )

      if ('error' in propertyUpdateResponse) {
        const message =
          typeof propertyUpdateResponse.error.message === 'string'
            ? propertyUpdateResponse.error.message
            : 'Something went wrong'

        throw new UpdateError(message)
      }

      if (propertyUpdateResponse && 'data' in propertyUpdateResponse) {
        // Replace updated property in the list of properties
        teamProperties.setProperties(
          teamProperties.properties.map((p) =>
            p.id === propertyUpdateResponse.data.id ? propertyUpdateResponse.data : p,
          ),
        )
      }

      const storeProperty = teamProperties.properties.find(
        (p) => p.id === propertyUpdateResponse.data.id,
      )
      if (storeProperty) {
        storeProperty.property_values = storeProperty.property_values.map((p) => {
          const propertyValue = propertyUpdateResponse.data.property_values.find(
            (pv) => pv.id === p.id,
          )
          return propertyValue || p
        })
      }

      toast.success({ meta: { title: UPDATE_PROPERTY_SUCCESS_MESSAGE } })

      return propertyUpdateResponse
    } catch (e: unknown) {
      if (e instanceof UpdateError) {
        toast.error({ meta: { title: e.message } })
        return
      }

      setContext('updateProperty error', { error: e })
      return
    }
  }

  const deleteProperty = async (
    id: string,
  ): Promise<Awaited<ReturnType<typeof deleteTeamProperty>> | void> => {
    if (!teamSlug.value) {
      return
    }

    const response = await deleteTeamProperty(teamSlug.value, id)

    if ('error' in response) {
      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      toast.error({ meta: { title: message } })
    } else {
      // The backend cascades soft-deletes to every descendant of `id`, so we
      // mirror that locally: strip the property and its full subtree from the
      // cache to avoid leaving orphaned children pointing at a dead parent.
      const toRemove = getDescendantIds(id, teamProperties.properties)
      toRemove.add(id)
      teamProperties.setProperties(teamProperties.properties.filter((p) => !toRemove.has(p.id)))
      toast.success({ meta: { title: DELETE_PROPERTY_SUCCESS_MESSAGE } })
    }
  }

  const deletePropertyValue = async (
    propertyId: string,
    propertyValueId: string,
  ): Promise<void> => {
    if (!teamSlug.value) {
      return
    }

    const response = await deleteTeamPropertyValue(teamSlug.value, propertyId, propertyValueId)

    if ('error' in response) {
      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      toast.error({ meta: { title: message } })

      throw new Error(message)
    }

    // Cascading semantics for `value_match` triggers:
    //   - If the trigger listed ONLY the deleted value, the child can no
    //     longer be triggered → soft-delete the child and its subtree.
    //   - If the trigger listed other values too, simply strip the deleted
    //     id from `property_value_ids` and keep the child alive — the
    //     remaining values can still satisfy it.
    // Children with `any_value` triggers are unaffected by a specific value
    // deletion.
    const allProperties = teamProperties.properties
    const idsToRemove = new Set<string>()
    const triggerPatches = new Map<string, string[]>()
    for (const p of allProperties) {
      if (p.parent_property_id !== propertyId) continue
      const trigger = p.trigger_condition
      if (!trigger || trigger.type !== 'value_match') continue
      if (!trigger.property_value_ids.includes(propertyValueId)) continue

      const remaining = trigger.property_value_ids.filter((id) => id !== propertyValueId)
      if (remaining.length === 0) {
        idsToRemove.add(p.id)
        for (const d of getDescendantIds(p.id, allProperties)) idsToRemove.add(d)
      } else {
        triggerPatches.set(p.id, remaining)
      }
    }

    teamProperties.setProperties(
      allProperties
        .filter((p) => !idsToRemove.has(p.id))
        .map((p) => {
          if (p.id === propertyId) {
            return {
              ...p,
              property_values: p.property_values.filter((v) => v.id !== propertyValueId),
            }
          }
          const patched = triggerPatches.get(p.id)
          if (patched) {
            return {
              ...p,
              trigger_condition: { type: 'value_match', property_value_ids: patched },
            }
          }
          return p
        }),
    )

    toast.success({ meta: { title: DELETE_PROPERTY_VALUE_SUCCESS_MESSAGE } })
  }

  const getItemPropertiesForItem = async (
    itemId: string,
    assignedStageInstance?: V2WorkflowStageInstancePayload,
  ): Promise<DatasetItemPropertiesResponse | undefined> => {
    if (!teamSlug.value) {
      return
    }

    const config = assignedStageInstance?.stage?.config

    // Check if this is a parallel blind stage (can have parallel_stages or parallel_stage_ids)
    const isParallelBlindStage =
      config && ('parallel_stages' in config || 'parallel_stage_ids' in config)

    // For parallel blind stages, check if user has admin privileges and can see all annotation groups
    if (isParallelBlindStage && auth.isAuthorized('view_full_datasets')) {
      // Get all annotation group IDs from the parallel stages
      let allAnnotationGroupIds: string[] = []

      if ('parallel_stages' in config) {
        // New format: array of objects with stage_id
        const parallelStagesConfig = config as { parallel_stages: { stage_id: string }[] }
        const parallelStageIds = parallelStagesConfig.parallel_stages.map((stage) => stage.stage_id)

        // Look up the actual stage objects and extract annotation_group_id from their configs
        allAnnotationGroupIds = parallelStageIds
          .map((stageId) => {
            const stage = workflowStore.workflowStages.find(
              (s: V2WorkflowStagePayload) => s.id === stageId,
            )
            // Check if stage has config with annotation_group_id (annotate/review stages)
            if (stage?.config && 'annotation_group_id' in stage.config) {
              return stage.config.annotation_group_id
            }
            return undefined
          })
          .filter((id): id is string => id !== undefined)
      } else if ('parallel_stage_ids' in config) {
        // Legacy format: array of strings
        const parallelStageIdsConfig = config as { parallel_stage_ids: string[] }
        const parallelStageIds = parallelStageIdsConfig.parallel_stage_ids

        // Look up the actual stage objects and extract annotation_group_id from their configs
        allAnnotationGroupIds = parallelStageIds
          .map((stageId) => {
            const stage = workflowStore.workflowStages.find(
              (s: V2WorkflowStagePayload) => s.id === stageId,
            )
            // Check if stage has config with annotation_group_id (annotate/review stages)
            if (stage?.config && 'annotation_group_id' in stage.config) {
              return stage.config.annotation_group_id
            }
            return undefined
          })
          .filter((id): id is string => id !== undefined)
      }

      const response = await getItemTeamItemProperties(
        teamSlug.value,
        itemId,
        allAnnotationGroupIds,
      )
      return response.data
    }

    // For regular annotate stages or regular users in parallel blind stages
    const shouldFilterByAnnotationGroup =
      config && 'annotation_group_id' in config && config.annotation_group_id

    if (shouldFilterByAnnotationGroup) {
      const annotationGroupId = (config as { annotation_group_id: string }).annotation_group_id

      const response = await getItemTeamItemProperties(teamSlug.value, itemId, [annotationGroupId])
      return response.data
    }

    const response = await getItemTeamItemProperties(teamSlug.value, itemId)
    return response.data
  }

  const setItemPropertyValue = async (
    itemId: string | undefined,
    propertyId: string,
    propertyValueIds: DatasetItemPropertyValues,
  ): Promise<ParsedError | ApiResponse<V2WorkflowCommandResponse> | undefined> => {
    if (!teamSlug.value) {
      return
    }

    if (!itemId) {
      console.warn('setItemPropertyValue called with undefined itemId')
      return
    }

    if (!workflowStore.selectedStageInstance?.id || !workflowStore.selectedStage?.id) {
      return
    }

    // For parallel blind stages, use the assigned stage instance instead of selected stage instance
    // This ensures we use the correct inner stage instance, not the parent PBS stage
    const stageInstance = workflowStore.assignedStageInstance || workflowStore.selectedStageInstance
    let annotationGroupId: string | null = null

    if (stageInstance?.stage?.config) {
      const config = stageInstance.stage.config
      if ('annotation_group_id' in config) {
        annotationGroupId = config.annotation_group_id || null
      }
    }

    // Use the actual stage instance for the command (assigned stage for PBS, selected stage otherwise)
    const stageInstanceId = stageInstance?.id || workflowStore.selectedStageInstance.id
    const stageId = stageInstance?.stage?.id || workflowStore.selectedStage.id

    // SSP whitelists top-level properties only; nested children inherit their
    // root ancestor's membership via `isPropertyAllowedByAncestry`. The post-API
    // forbidden-property handler below still catches stale-config races.
    const isAnnotateStage = stageInstance?.stage?.type === StageType.Annotate
    if (isAnnotateStage && stageSpecificClasses.isFilterApplied.value) {
      const allowedSet = new Set(stageSpecificClasses.allowedPropertyIds.value)
      const property = teamProperties.properties.find((p) => p.id === propertyId)
      const isPropertyAllowed = property
        ? isPropertyAllowedByAncestry(property, teamProperties.properties, allowedSet)
        : allowedSet.has(propertyId)
      if (!isPropertyAllowed) {
        toast.error({
          meta: {
            title:
              'This property is not allowed for the current annotate stage. Please contact your admin if the stage configuration was recently changed.',
          },
        })
        return
      }
    }

    // Store the previous value to revert on error
    const previousProperty = teamProperties.currentItemProperties.find(
      (prop) => prop.id === propertyId,
    )
    const previousValues = previousProperty?.values

    // Optimistic UI: updates the current item team property value
    teamProperties.setCurrentItemPropertyValues(propertyId, propertyValueIds)
    const generation = useStageView().generation

    const response = await setItemTeamPropertyValue(
      teamSlug.value,
      itemId,
      propertyId,
      propertyValueIds,
      stageInstanceId,
      stageId,
      annotationGroupId,
    )

    // If the API call failed, revert the optimistic update
    // Check for both ApiResult format (ok: false) and ParsedError format (error property)
    const hasError =
      !response ||
      (response && 'ok' in response && response.ok === false) ||
      (response && 'error' in response && response.error)

    if (hasError) {
      // Check if this is a forbidden property error (422 with forbidden_property_ids)
      // This can happen due to race conditions when stage config changes while annotator is working
      let errorMessage =
        response && 'error' in response && response.error
          ? getErrorMessage(response.error).join(', ')
          : 'Failed to set property value. Please try again.'

      // Check if error message indicates forbidden property
      const isForbiddenPropertyError =
        errorMessage.toLowerCase().includes('forbidden') ||
        (response &&
          'error' in response &&
          response.error &&
          'isValidationError' in response.error &&
          response.error.isValidationError &&
          'forbidden_property_ids' in response.error)

      if (isForbiddenPropertyError) {
        errorMessage =
          'This property is no longer allowed for the current annotate stage. The stage configuration may have been changed. Please refresh the page or contact your admin.'
      }

      toast.error({ meta: { title: errorMessage } })

      // the item values on screen now belong to another stage visit (e.g. time travel)
      if (useStageView().generation !== generation) {
        return response
      }

      // Revert to previous value or remove if it didn't exist
      if (previousValues !== undefined) {
        teamProperties.setCurrentItemPropertyValues(propertyId, previousValues)
      } else {
        // Remove the property if it didn't exist before
        teamProperties.currentItemProperties = teamProperties.currentItemProperties.filter(
          (prop) => prop.id !== propertyId,
        )
      }

      // Refresh properties from server to ensure consistency
      // Use a per-itemId lock to prevent race conditions when multiple property updates fail concurrently
      if (!pendingPropertyRefreshes.value.has(itemId)) {
        pendingPropertyRefreshes.value.add(itemId)
        try {
          const refreshedProperties = await getItemPropertiesForItem(itemId, stageInstance)
          if (refreshedProperties && useStageView().generation === generation) {
            teamProperties.currentItemProperties = refreshedProperties.properties
          }
        } catch (error) {
          // If refresh fails, log but don't throw - we've already reverted the optimistic update
          setContext('property_refresh_error', { error: String(error) })
        } finally {
          // Always remove from pending set, even if refresh fails
          pendingPropertyRefreshes.value.delete(itemId)
        }
      }
    }

    return response
  }

  /**
   * Differs from {@link setItemPropertyValue}:
   *  - always writes to the default group (`annotation_group_id: null`)
   *  - skips the annotate-stage forbidden-property gate
   *  - does not optimistically mutate `currentItemProperties`
   */
  const resolveItemPropertyConflict = async (
    itemId: string | undefined,
    propertyId: string,
    propertyValueIds: string[],
  ): Promise<ParsedError | ApiResponse<V2WorkflowCommandResponse> | undefined> => {
    if (!teamSlug.value) {
      return
    }
    if (!itemId) {
      console.warn('resolveItemPropertyConflict called with undefined itemId')
      return
    }
    if (!workflowStore.selectedStageInstance?.id || !workflowStore.selectedStage?.id) {
      return
    }

    const stageInstance = workflowStore.assignedStageInstance || workflowStore.selectedStageInstance
    const stageInstanceId = stageInstance?.id || workflowStore.selectedStageInstance.id
    const stageId = stageInstance?.stage?.id || workflowStore.selectedStage.id

    const response = await setItemTeamPropertyValue(
      teamSlug.value,
      itemId,
      propertyId,
      propertyValueIds,
      stageInstanceId,
      stageId,
      null,
    )

    const hasError =
      !response ||
      (response && 'ok' in response && response.ok === false) ||
      (response && 'error' in response && response.error)

    if (hasError) {
      const errorMessage =
        response && 'error' in response && response.error
          ? getErrorMessage(response.error).join(', ')
          : 'Failed to resolve disagreement. Please try again.'
      toast.error({ meta: { title: errorMessage } })
    }

    return response
  }

  const isValidPropertyResponse = (
    response: { data: TeamProperty | TeamItemProperty } | ParsedError | void,
  ): response is { data: TeamProperty | TeamItemProperty } => !!response && !('error' in response)

  return {
    teamSlug,
    isValidPropertyResponse,
    createProperty,
    updateProperty,
    deleteProperty,
    updatePropertyValue,
    updatePropertyValuesOrder,
    deletePropertyValue,
    setItemPropertyValue,
    resolveItemPropertyConflict,
    getItemPropertiesForItem,

    defaultPropertyGranularity,
  }
})
